import { useState } from 'react'
import { Copy, Check, Users, Link2, Share2 } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../services/api'

export default function FamilyPage() {
  const { user } = useAuthStore()
  const isParent = user?.role === 'parent'

  // Parent state
  const [code, setCode] = useState<string | null>(null)
  const [deepLink, setDeepLink] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)

  // Student state
  const [linkCode, setLinkCode] = useState('')
  const [linking, setLinking] = useState(false)
  const [linked, setLinked] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const data = await api.generateLinkCode()
      setCode(data.code)
      setDeepLink(data.deep_link)
    } catch {
      alert('No se pudo generar el código. Intenta de nuevo.')
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    if (!code) return
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCopyLink = () => {
    if (!deepLink) return
    navigator.clipboard.writeText(deepLink)
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleShareWhatsApp = () => {
    if (!deepLink) return
    const text = encodeURIComponent(`Entrá a Kapri con este link: ${deepLink}`)
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer')
  }

  const handleUseCode = async () => {
    if (!linkCode.trim()) return
    setLinking(true)
    setLinkError(null)
    try {
      await api.useLinkCode(linkCode.trim())
      setLinked(true)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Código inválido o expirado'
      setLinkError(msg)
    } finally {
      setLinking(false)
    }
  }

  return (
    <div className="max-w-xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Familia</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          {isParent
            ? 'Genera un código para vincular a tus hijos'
            : 'Usa el código de tu padre o tutor para vincular tu cuenta'}
        </p>
      </div>

      {isParent ? (
        /* ---- Parent view ---- */
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.2)' }}
            >
              <Users className="w-5 h-5" style={{ color: '#818CF8' }} />
            </div>
            <div>
              <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Vincular estudiante</h2>
              <p className="text-xs" style={{ color: '#64748B' }}>El código expira en 24 horas</p>
            </div>
          </div>

          {code ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#475569' }}>
                Código generado
              </p>
              <div
                className="flex items-center justify-between rounded-xl px-4 py-3 mb-4"
                style={{ background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
              >
                <span
                  className="text-2xl font-bold tracking-widest"
                  style={{ color: '#A5B4FC', fontFamily: 'monospace' }}
                >
                  {code}
                </span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
                  style={{ color: copied ? '#10B981' : '#818CF8', background: 'rgba(255,255,255,0.06)' }}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <p className="text-xs mb-4" style={{ color: '#475569' }}>
                Comparte el link con tu hijo. Cuando lo abra, quedará vinculado a tu cuenta automáticamente.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={handleShareWhatsApp}
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors"
                  style={{
                    background: 'rgba(37,211,102,0.12)',
                    border: '1px solid rgba(37,211,102,0.3)',
                    color: '#4ADE80',
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  Compartir por WhatsApp
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl transition-colors"
                  style={{ color: copiedLink ? '#10B981' : '#818CF8', background: 'rgba(255,255,255,0.06)' }}
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {copiedLink ? 'Copiado' : 'Copiar link'}
                </button>
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-secondary text-sm disabled:opacity-60 w-full"
              >
                Generar nuevo código
              </button>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-primary flex items-center gap-2 disabled:opacity-60"
            >
              <Link2 className="w-4 h-4" />
              {generating ? 'Generando...' : 'Generar código de vinculación'}
            </button>
          )}
        </div>
      ) : (
        /* ---- Student view ---- */
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.2)' }}
            >
              <Link2 className="w-5 h-5" style={{ color: '#818CF8' }} />
            </div>
            <div>
              <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Usar código de vinculación</h2>
              <p className="text-xs" style={{ color: '#64748B' }}>Pide el código a tu padre o tutor</p>
            </div>
          </div>

          {linked ? (
            <div
              className="rounded-xl p-4 flex items-center gap-3"
              style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}
            >
              <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#10B981' }} />
              <p className="text-sm" style={{ color: '#10B981' }}>
                ¡Cuenta vinculada correctamente! Tu tutor puede ver tu progreso.
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={linkCode}
                  onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                  placeholder="Ej: ABC12345"
                  maxLength={12}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-mono"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${linkError ? '#EF4444' : 'rgba(255,255,255,0.1)'}`,
                    color: '#F1F5F9',
                    outline: 'none',
                    letterSpacing: '0.1em',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#4F46E5' }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = linkError ? '#EF4444' : 'rgba(255,255,255,0.1)' }}
                />
                <button
                  onClick={handleUseCode}
                  disabled={linking || !linkCode.trim()}
                  className="btn-primary disabled:opacity-60"
                >
                  {linking ? 'Vinculando...' : 'Vincular'}
                </button>
              </div>
              {linkError && (
                <p className="text-xs mt-2" style={{ color: '#EF4444' }}>{linkError}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
